import { UNIT_CATALOG } from "@repo/domain";
import { Button } from "@repo/ui/components/ui/button";
import { Label } from "@repo/ui/components/ui/label";
import { Plus, Trash2 } from "lucide-react";
import { type ClinicalValueFormValues, EMPTY_CLINICAL_VALUE } from "@/features/catalog/forms/clinical-definition-form";
import { withFieldGroup } from "@/shared/forms/app-form";

const DEFAULT_DISABLED_PROPS: { disabled?: boolean; optionalLimits?: boolean } = {};
const VALUE_KIND_OPTIONS = ["Quantity", "Integer", "Choice", "Boolean", "Text"].map((kind) => ({
  value: kind,
  label: kind,
}));

// Picked, never typed. "" is unitless and comes first, because a count or a
// score is a quantity with nothing to measure it in.
const UNIT_OPTIONS = [
  { value: "", label: "No unit" },
  // Symbol AND name: a list of bare symbols cannot say whether `in` is an inch
  // or which `deg` is meant, and UCUM's near-homographs punish a guess.
  ...UNIT_CATALOG.map((unit) => ({
    value: unit.code,
    label: unit.display === unit.name ? unit.name : `${unit.display} — ${unit.name}`,
  })),
];

export const ClinicalValueEditor = withFieldGroup({
  defaultValues: EMPTY_CLINICAL_VALUE satisfies ClinicalValueFormValues,
  props: DEFAULT_DISABLED_PROPS,
  render: function Render({ group, disabled, optionalLimits }) {
    return (
      <div className="space-y-3">
        <group.AppField name="kind">
          {(field) => <field.SelectField label="Value type" options={VALUE_KIND_OPTIONS} disabled={disabled} />}
        </group.AppField>
        <group.Subscribe selector={(state) => state.values}>
          {(value) => {
            const kind = value?.kind;
            // A group outlives its slot by a render when the array around it
            // shrinks: removing a component leaves this mounted on
            // `components[last].value`, which no longer exists. There is
            // nothing to edit, so render nothing rather than read off nothing.
            if (!kind) return null;
            if (kind === "Quantity" || kind === "Integer") {
              return (
                <div className="space-y-4">
                  {kind === "Quantity" && (
                    <group.AppField name="unitCode">
                      {(field) => <field.SelectField label="Unit" options={UNIT_OPTIONS} disabled={disabled} />}
                    </group.AppField>
                  )}
                  <section className="border-t pt-3">
                    <h4 className="text-sm font-medium">Value limits</h4>
                    <div className="grid grid-cols-2 gap-3 pt-3">
                      <group.AppField name="min">
                        {(field) => (
                          <field.NumberField
                            label="Minimum"
                            step={kind === "Integer" ? 1 : "any"}
                            format={kind === "Integer" ? { maximumFractionDigits: 0 } : undefined}
                            isRequired={!optionalLimits}
                            hideRequiredIndicator
                            disabled={disabled}
                          />
                        )}
                      </group.AppField>
                      <group.AppField name="max">
                        {(field) => (
                          <field.NumberField
                            label="Maximum"
                            step={kind === "Integer" ? 1 : "any"}
                            format={kind === "Integer" ? { maximumFractionDigits: 0 } : undefined}
                            isRequired={!optionalLimits}
                            hideRequiredIndicator
                            disabled={disabled}
                          />
                        )}
                      </group.AppField>
                      <group.AppField name="step">
                        {(field) => (
                          <field.NumberField
                            label="Step"
                            step={kind === "Integer" ? 1 : "any"}
                            format={kind === "Integer" ? { maximumFractionDigits: 0 } : undefined}
                            isRequired={!optionalLimits && kind === "Quantity"}
                            hideRequiredIndicator
                            disabled={disabled}
                          />
                        )}
                      </group.AppField>
                    </div>
                  </section>
                </div>
              );
            }
            if (kind === "Choice") {
              return (
                <div className="space-y-3">
                  <group.AppField name="cardinality">
                    {(field) => (
                      <field.OptionGroupField
                        label="Selection behavior"
                        options={[
                          { value: "Single", label: "Choose one" },
                          { value: "Multiple", label: "Choose multiple" },
                        ]}
                        disabled={disabled}
                      />
                    )}
                  </group.AppField>
                  <group.Field name="options" mode="array">
                    {(field) => (
                      <div className="space-y-2 border-l-2 pl-3">
                        <div className="flex items-center justify-between">
                          <Label className="text-xs text-muted-foreground">Options</Label>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => field.pushValue({ id: "", label: "" })}
                            disabled={disabled}
                          >
                            <Plus className="size-3" /> Add option
                          </Button>
                        </div>
                        {field.state.value.map((option, index) => (
                          <div key={option.id || index} className="flex items-end gap-2">
                            <group.AppField name={`options[${index}].label`}>
                              {(optionField) => (
                                <div className="flex-1">
                                  <optionField.InputField label={`Option ${index + 1}`} disabled={disabled} />
                                </div>
                              )}
                            </group.AppField>
                            <RemoveButton onClick={() => field.removeValue(index)} disabled={disabled} />
                          </div>
                        ))}
                      </div>
                    )}
                  </group.Field>
                </div>
              );
            }
            return null;
          }}
        </group.Subscribe>
      </div>
    );
  },
});

function RemoveButton({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) {
  return (
    <Button type="button" variant="ghost" size="icon" onClick={onClick} disabled={disabled}>
      <Trash2 className="size-3" />
    </Button>
  );
}

export function resetValueKind(kind: ClinicalValueFormValues["kind"]): ClinicalValueFormValues {
  return { ...structuredClone(EMPTY_CLINICAL_VALUE), kind };
}
